import type { ChargingCardsSchemaType } from '@/components/steps/ChargingCardsStep';
import type { InvoiceDataSchemaType } from '@/components/steps/InvoicingStep/invoiceDataSchema';
import { ADDRESS_TYPE } from '@/components/steps/ShippingAddressStep/shippingSchema';
import { createDebugConfig, trackCustomEvent } from '@/configuration/setup/googleAnalytics';
import { getTimeSpent, getTotalTime } from '@/features/onboarding/utils/timeTracking';
import { RFID_FLOW_NAME } from '@/features/orderNewCards/utils/analytics';

export const ONBOARDING_FLOW_NAME = 'onboarding';

export const trackStepView = (stepNumber: number, stepName: string) => {
    trackCustomEvent(
        'step_view',
        {
            flow_name: ONBOARDING_FLOW_NAME,
            step_number: stepNumber,
            step_name: stepName,
            element_name: 'stepper',
            trigger: 'click',
        },
        createDebugConfig()
    );
};

export const trackStepAbandonment = (stepNumber: number, stepName: string) => {
    trackCustomEvent(
        'step_abandonment',
        {
            flow_name: ONBOARDING_FLOW_NAME,
            step_number: stepNumber,
            step_name: stepName,
            time_spent: getTimeSpent(ONBOARDING_FLOW_NAME, stepNumber),
            element_name: 'stepper',
            trigger: 'visibility',
        },
        createDebugConfig()
    );
};

export const trackFlowComplete = (nextDestination: string, additionalData = {}) => {
    trackCustomEvent(
        `${ONBOARDING_FLOW_NAME}_complete`,
        {
            flow_name: ONBOARDING_FLOW_NAME,
            next_destination: nextDestination,
            total_time: getTotalTime(ONBOARDING_FLOW_NAME),
            element_name: 'complete',
            trigger: 'click',
            ...additionalData,
        },
        createDebugConfig()
    );
};

export const trackChargingCards = (data: ChargingCardsSchemaType) => {
    trackCustomEvent(
        'charging_cards_submitted',
        {
            flow_name: ONBOARDING_FLOW_NAME,
            cards_count: data.cardsNumber,
            has_customization: data.cardsListCustomization.some(card => card.line1 || card.line2),
            element_name: 'charge_cards_form',
            trigger: 'click',
        },
        createDebugConfig()
    );
};

export const trackInvoicingData = (data: InvoiceDataSchemaType) => {
    trackCustomEvent(
        'company_and_invoicing_data',
        {
            flow_name: ONBOARDING_FLOW_NAME,
            country: data.invoicing.country,
            has_additional_vat: data.invoicing.additional.length > 0,
            element_name: 'invoicing_form',
            trigger: 'click',
        },
        createDebugConfig()
    );
};

export const trackButtonClick = (buttonName: string, destination: string) => {
    trackCustomEvent(
        'button_click',
        {
            flow_name: ONBOARDING_FLOW_NAME,
            button_name: buttonName,
            destination,
            element_name: 'button',
            trigger: 'click',
        },
        createDebugConfig()
    );
};

export const trackApplyToAllClick = (isOrderNewCard: boolean, line1: string, numberOfCards: number) => {
    trackCustomEvent(
        'apply_to_all_click',
        {
            flow_name: isOrderNewCard ? RFID_FLOW_NAME : ONBOARDING_FLOW_NAME,
            line1Value: line1,
            numberOfCards: numberOfCards,
            element_name: 'button',
            trigger: 'click',
        },
        createDebugConfig()
    );
};

export const trackSummarySubmit = (
    isOrderNewCard: boolean,
    hasRecipient: boolean,
    addressType?: (typeof ADDRESS_TYPE)[keyof typeof ADDRESS_TYPE]
) => {
    let elementState: string;

    if (addressType === ADDRESS_TYPE.individual) {
        elementState = ADDRESS_TYPE.individual;
    } else {
        elementState = hasRecipient ? 'company_with_recipient' : 'company_without_recipient';
    }

    trackCustomEvent(
        'shipping_address_summary_submit',
        {
            flow_name: isOrderNewCard ? RFID_FLOW_NAME : ONBOARDING_FLOW_NAME,
            element_name: 'shipping_address',
            element_state: elementState,
            trigger: 'click',
        },
        createDebugConfig()
    );
};
